/** @copyright (c) Viewpost. All Rights Reserved. See LICENSE for more details. */

import React, { useEffect } from 'react';

import Icon, { IconTypes } from 'components/Icon';
import LoadingIndicator from 'components/LoadingIndicator/Base';

let OPENED_WINDOW = null;

const LOADING_URL = `${window.location.origin}/callback/loading`;

/**
 * Safari (iOS) is strict about opening popups.
 * New tabs/windows can only be opened directly from a user interaction (onClick) function.
 * To get this to work in Safari, the click event that will trigger the render
 * of CallbackOpener must call this directly first.
 * Once the window is open, CallbackOpener can update its url as needed
 */
export const openCallbackWindow = (url) => {
  OPENED_WINDOW = window.open(url ?? LOADING_URL, '_blank');
};

/**
 * Opens a new tab to a url.
 * If/when that url/site redirects back to the landing page component below,
 * it will fire the onComplete and close the new tab.
 */
export const CallbackOpener = ({
  url,
  onComplete,
  children
}) => {

  // listen for landing page message post
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event?.data?.action === 'complete') {
        OPENED_WINDOW?.close(); // close the tab we opened
        onComplete?.();
      }
    };

    window.addEventListener('message', handleMessage);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // poll for tab changes
  useEffect(() => {
    const interval = setInterval(() => {

      if (OPENED_WINDOW?.closed) {
        // if window was manually closed
        clearInterval(interval);
        onComplete?.();
      }

      try {
        // May throw when cross-origin, should succeed when same-origin
        const currentUrl = OPENED_WINDOW.location.href;

        if (currentUrl !== LOADING_URL
          && currentUrl?.startsWith(`${window.location.origin}/callback`)) {
          // window was navigated to landing page
          clearInterval(interval);
          OPENED_WINDOW?.close(); // close the tab we opened
          onComplete?.();
        }
      } catch (e) {
        // Probably still cross-origin, keep polling
      }
    }, 1000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);

  // open or update new tab
  useEffect(() => {
    if (url) {
      if (!OPENED_WINDOW || OPENED_WINDOW?.closed) {
        // open new window
        OPENED_WINDOW = window.open(url, '_blank');
      } else {
        // navigate opened window
        OPENED_WINDOW.location.href = url;
      }
    }
  }, [url]);

  if (!url) return null;

  // allow caller to present custom waiting message
  return children;
};

/**
 * An init
 */
export const CallbackLoadingPage = () => {
  return (
    <div style={{ height: '100%' }} className="viewstrap">
      <div style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <LoadingIndicator>
            Loading...
          </LoadingIndicator>
        </div>
      </div>
    </div>
  );
};

/**
 * This page is intended to be loaded by 3rd party sites to redirect control back to us
 * when they are finished.
 *
 * Use the CallbackOpener component above in app to kick off the redirection to the other site.
 *
 * Example:
 * We open a new tab using CallbackOpener to one of our partner's sites and
 * either configure OR pass the URL to this Landing Page in the URL's query string.
 * When the other site is finished its work, it redirects back to this landing page component
 * which sends the completion message to the Opener component.
 */
const CallbackLandingPage = () => {

  useEffect(() => {
    window.opener?.postMessage({ action: 'complete' }, window.location.origin);
  });

  return (
    <div style={{ height: '100%' }} className="viewstrap">
      <div style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{textAlign: 'center'}}>
          <Icon type={IconTypes.CircleCheck} size={50} color="green" />
          <br/><br/>
        </div>
      </div>
    </div>
  );
};

export default CallbackLandingPage;